import { Autocomplete, Checkbox, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {Controller, useController} from 'react-hook-form';
import React, { useState, useCallback } from 'react';
import MySearchIcon from '../components/MySearchIcon';

// 한글 초성 추출
const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const getChosung = str => [...str].map(c => {
    const code = c.charCodeAt(0) - 0xAC00;
    return code >= 0 && code <= 11171 ? CHO[Math.floor(code / 588)] : c;
}).join('');
const isChosung = str => [...str].every(c => CHO.includes(c));

const FormInputAllSearch = ({
                                name,
                                control,
                                disabled = false,
                                options,
                                selectedDate,
                                rules,
                                placeholder,
                                handleChange,
                                type = 'text',
                                size = 'small',
                                sx,
                                noOptionsText,
                                renderTags,
                                keepInputOnSelect = false, // true면 옵션 선택 후에도 검색어 유지
                                hideAllOptionWhenAllSelected = false, // true면 모든 항목 선택 시 '전체 선택' 항목을 목록에서 숨김
                            }) => {
    const {field, fieldState: { error },} = useController({ name, control });

    // keepInputOnSelect가 true일 때만 inputValue를 수동 관리
    const [inputValue, setInputValue] = useState('');
    const [open, setOpen] = useState(false);
    const handleInputChange = (_, newValue, reason) => {
        // 'reset'은 옵션 선택 시 MUI가 자동으로 input을 비우려 할 때 발생
        // 이걸 무시하면 선택 후에도 검색어가 유지됨
        if (reason === 'reset' && keepInputOnSelect) return;
        setInputValue(newValue);
    };

    const allOption = { id: 'ALL', name: '전체 선택' };

    // 모든 항목이 선택됐는지 여부 (allOption은 field.value에 담기지 않으므로 실제 항목 수로 판별)
    const isAllRealSelected = options?.length > 0 && options.every(opt => field.value?.includes(opt.id));

    const realOptions = [allOption, ...options];
    const selectedOptions = realOptions.filter(opt => field.value?.includes(opt.id));

    // 초성 검색 지원 filterOptions
    const filterOptions = useCallback((opts, { inputValue: query }) => {
        if (!query) return opts;
        const q = query.toLowerCase();
        return opts.filter(opt => {
            if (opt.id === 'ALL') return true;
            const name = opt.name.toLowerCase();
            if (name.includes(q)) return true;
            if (isChosung(q)) return getChosung(opt.name).includes(q);
            return false;
        });
    }, []);

    const onSelectChange = (_, selected) => {
        const selectedIds = selected.map(opt => opt.id);
        const optionIds = options.map(opt => opt.id);

        if (selectedIds.includes('ALL')) {
            const isAllSelected = field.value?.length === options.length;
            const newIds = isAllSelected ? [] : optionIds;
            field.onChange(newIds);
            handleChange?.(options);
        } else {
            const filtered = selected.filter(opt => opt.id !== 'ALL');
            const newIds = filtered.map(opt => opt.id);
            field.onChange(newIds);
            handleChange?.(selected);
        }
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
            <Autocomplete
                {...field}
                multiple
                disablePortal
                disabled={disabled}
                size={size}
                disableCloseOnSelect
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={selectedOptions}
                options={realOptions}
                popupIcon={<MySearchIcon />}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={filterOptions}
                noOptionsText={noOptionsText || '선택할 항목이 없습니다'}
                onChange={onSelectChange}
                {...(keepInputOnSelect && {
                    inputValue,
                    onInputChange: handleInputChange,
                })}
                {...(renderTags && { renderTags: (value, getTagProps) => renderTags(value, getTagProps, selectedOptions) })}
                renderOption={(props, option, { selected }) => {
                    const isChecked = option.id === 'ALL'
                        ? isAllRealSelected
                        : field.value?.includes(option.id);
                    return (
                        <li {...props} key={option.id}>
                            <Checkbox
                                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                checked={isChecked}
                                sx={{ mr: 1, p: 0, color: '#0F7F85', '&.Mui-checked': { color: '#0F7F85' } }}
                            />
                            {option.name}
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        type={type}
                        {...params}
                        placeholder={selectedOptions?.length > 0 ? '' : placeholder}
                        error={!!error}
                        helperText={error?.message}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#0F7F85',
                                },
                            },
                        }}
                    />
                )}
                sx={{
                    ...sx,
                    '& .MuiAutocomplete-popupIndicator': { transform: 'none' },
                }}
            />
            )}
        />
    );
};

export default FormInputAllSearch;